import React, { useState, useEffect } from 'react';
import InfiniteSelect from '../../../../../components/InfiniteSelect';
import axios from '../../../../../config/axios';
import { FormGroup, Label } from '../style';

interface SteelOrdersInfiniteSelectProps {
  items: { id: number; name: string }[];
  setItems: React.Dispatch<React.SetStateAction<{ id: number; name: string }[]>>;
  endpoint: string;
  placeholder?: string;
  searchPlaceholder?: string;
  label?: string;
  isDisabled?: boolean;
  additionalParams?: Record<string, any>;
  customOptions?: { id: number; name: string }[];
  onCleared?: () => void;
}

const SteelOrdersInfiniteSelect: React.FC<SteelOrdersInfiniteSelectProps> = ({
  endpoint,
  items,
  setItems,
  label,
  placeholder,
  searchPlaceholder,
  isDisabled,
  additionalParams,
  ...props
}) => {
  const API_BASE_URL = `${process.env.REACT_APP_GATEWAY_BASE_URL}/steel-orders`;
  
  const fetchFunction = async (limit: number, offset: number, search?: string) => {
    if (props.customOptions) return props.customOptions;
    try {
      const params: any = { limit };
      if (typeof search === 'string' && /^\d+$/.test(search)) {
        if (endpoint === 'scripts') {
          params.id = Number(search);
        } else if (endpoint === 'resource-suppliers') {
          params.companyId = Number(search);
        } else if (endpoint === 'service-suppliers') {
          params.companyId = Number(search);
        } else {
        params.id = Number(search);
        }
      } else if (search) {
        params.search = search;
      }

      const page = Math.floor(offset / limit) + 1;
      params.page = page;

      if (additionalParams && typeof additionalParams === 'object') {
        Object.assign(params, additionalParams);
        if (endpoint === 'scripts' && (additionalParams as any).productId) {
          const pid = (additionalParams as any).productId;
          params.product_id = pid;
          params.finalProductId = pid;
          params.final_product_id = pid;
          params.product = pid;
        }
      }

      if (endpoint === 'scripts') {
        const hasProductParam = params.productId || params.product_id || params.finalProductId || params.final_product_id || params.product;
        if (!hasProductParam) {
          return [];
        }
      }

      if (endpoint === 'scripts') {
        params.offset = offset;
        delete params.page;
      }

      const response = await axios.get(`${API_BASE_URL}/select-in-order/${endpoint}`, { params });
      let data = [];
      if (response.data.services) {
        data = response.data.services;
      } else if (response.data.resources) {
        data = response.data.resources;
      } else if (response.data.suppliers) {
        data = response.data.suppliers;
      } else if (response.data.products) {
        data = response.data.products;
      } else if (response.data.stocks) {
        data = (response.data.stocks || []).map((s: any) => ({
          id: s.id,
          name: s.description || s.name || '',
          codeId: s.codeId,
          description: s.description,
          fabrication: s.fabrication,
          format: s.format,
          heatTreatment: s.heatTreatment,
          ncm: s.ncm,
          ncmId: s.ncmId,
          originCountry: s.originCountry,
          supplier: s.supplier,
          quantity: s.quantity,
          totalWeight: s.totalWeight,
          measurementUnit: s.measurementUnit,
          costPerKg: s.costPerKg,
        }));
      } else if (response.data.scripts) {
        data = response.data.scripts;
      } else if (Array.isArray(response.data)) {
        data = response.data;
      } else {
        data = [];
      }

      if (endpoint === 'materials' && Array.isArray(data)) {
        data = data.map((m: any) => ({
          ...m,
          density: m?.density ?? null,
        }));
      }

      if (endpoint === 'scripts') {
        const mapped = Array.isArray(data)
          ? data.map((s: any, idx: number) => {
              if (typeof s === 'string') {
                return { id: idx + 1, name: s };
              }
              return { id: s?.id ?? idx + 1, name: String(s?.name ?? '') };
            })
          : [];
        return mapped;
      }

      const formattedData = Array.isArray(data) ? data.map((item: any) => ({
        id: item.id,
        name: item.name,
        ...item
      })) : [];
      

      return formattedData;
    } catch (error) {
      console.error('Erro ao buscar dados:', error);
      return [];
    }
  };

  const handleSelect = (option: any) => {
    if (option) {
      setItems([option]);
    } else {
      setItems([]);
      if (props.onCleared) {
        try { props.onCleared(); } catch (e){
        // Ignore errors
        }
      }
    }
  };

  const selectedValueRaw = items.length > 0 ? items[0] : null;
  const selectedValue = isDisabled ? null : selectedValueRaw;

  const getStockFieldsConfig = () => {
    if (endpoint === 'stocks') {
      return [
        {
          key: 'quantity',
          label: 'Qtd. (pç)',
          getValue: (option: any) => option.quantity,
          render: (value: any) => value ? `${parseFloat(value).toFixed(2)}` : '',
          condition: (option: any) => option.quantity !== undefined && option.quantity !== null
        },
        {
          key: 'totalWeight',
          label: 'Peso (kg)',
          getValue: (option: any) => option.totalWeight,
          render: (value: any) => value ? `${parseFloat(value).toFixed(2)}` : '',
          condition: (option: any) => option.totalWeight !== undefined && option.totalWeight !== null
        },
        {
          key: 'format',
          label: 'Formato',
          getValue: (option: any) => option.format,
          condition: (option: any) => option.format
        },
        {
          key: 'fabrication',
          label: 'Fabricação',
          getValue: (option: any) => option.fabrication,
          condition: (option: any) => option.fabrication
        },
        {
          key: 'heatTreatment',
          label: 'Trat. Térmico',
          getValue: (option: any) => option.heatTreatment,
          condition: (option: any) => option.heatTreatment
        },
        {
          key: 'supplier',
          label: 'Fornecedor',
          getValue: (option: any) => option.supplier,
          condition: (option: any) => option.supplier
        }
      ];
    }
    return [];
  };

  return (
    <FormGroup>
      {label && <Label>{label}</Label>}
      <InfiniteSelect
        value={selectedValue}
        onSelect={handleSelect}
        placeholder={placeholder}
        searchPlaceholder={searchPlaceholder}
        fetchFunction={fetchFunction}
        disabled={isDisabled}
        mode="readonly"
        fieldsConfig={getStockFieldsConfig()}
        showDefaultFields={true}
        getOptionLabel={(opt: any) => {
          if (endpoint === 'materials') {
            return opt.name || '';
          }
          if (endpoint === 'stocks') {
            return opt.description || opt.name || '';
          }
          return opt.name;
        }}
      />
    </FormGroup>
  );
};

export default SteelOrdersInfiniteSelect;
